import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';
import * as XLSX from 'xlsx';

declare const bootstrap: any;

interface User {
    _id: string;
    firstName: string;
    middleName: string;
    lastName: string;
    name: string;
    whatsAppNo: string;
    userType: 'internal' | 'external';
    organisationName: string;
    stopMsg: boolean;
    masterL2Ids: string[];
    userName?: string;
    hasLogin?: boolean;
    // internal-only
    empNo?: string;
    esiNo?: string;
    dob?: string;
    department?: string;
    role?: string;
    bu?: string;
}

interface GenericGroup {
    master: { _id: string; masterName: string; fieldLabel: string; userTypeScope?: 'internal' | 'external' | 'none' | 'disabled' };
    values: { _id: string; value: string }[];
    selectedId: string;
}

const EMPTY_USER = () => ({
    firstName: '', middleName: '', lastName: '',
    whatsAppNo: '', userType: 'external' as 'internal' | 'external',
    organisationName: '', stopMsg: false,
    empNo: '', esiNo: '', dob: '', department: '', role: '', bu: '',
});

@Component({
    selector: 'app-user-master',
    standalone: true,
    imports: [CommonModule, FormsModule, VioTablesV2Component],
    templateUrl: './user-master.component.html',
    styleUrl: './user-master.component.scss',
})
export class UserMasterComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;
    @ViewChild('addModal') addModalRef?: ElementRef<HTMLElement>;
    @ViewChild('editModal') editModalRef?: ElementRef<HTMLElement>;
    @ViewChild('payslipModal') payslipModalRef?: ElementRef<HTMLElement>;
    @ViewChild('credentialsModal') credentialsModalRef?: ElementRef<HTMLElement>;

    users: User[] = [];
    isLoadingUsers = false;

    // Generic master groups (custom masters)
    genericGroups: GenericGroup[] = [];
    isLoadingGroups = false;

    // Add form
    newUser = EMPTY_USER();
    addGroups: GenericGroup[] = [];
    isSavingAdd = false;
    addError = '';

    // Edit form
    editUser: ReturnType<typeof EMPTY_USER> & { _id: string } = { _id: '', ...EMPTY_USER() };
    editGroups: GenericGroup[] = [];
    isSavingEdit = false;
    editError = '';

    // Payslip modal
    payslipTitle = '';
    employeePayslips: any[] = [];
    isLoadingPayslips = false;
    payslipError = '';

    // Bulk import
    isDownloadingTemplate = false;
    isUploading = false;
    uploadSummary: { inserted: number; errors: { row: number; message: string }[] } | null = null;

    // Credentials modal
    credUser: { _id: string; name: string } = { _id: '', name: '' };
    credForm = { userName: '', pwd: '', showPwd: false };
    isSavingCred = false;
    credError = '';

    private addModalInst: any;
    private editModalInst: any;
    private payslipModalInst: any;
    private credModalInst: any;

    payslipTableConfig: VT2_TABLE_CONFIG = {
        tableTitle: '',
        excelFileName: 'employee-payslips',
        columnLabels: {
            documentType: 'Document Type',
            payslipForMonth: 'Month',
            payslipForYear: 'Year',
            uploadedBy: 'Uploaded By',
            createdAt: 'Uploaded On',
        },
        excludeFields: ['_id', 's3Key', 'employeeName', 'empNo', 'esiNo'],
        dateFields: ['createdAt'],
        isSortable: true,
        showFormulaButton: false,
        showSummationButton: false,
        showHeaderGroupButton: false,
        actionButtons: [
            {
                icon: '👁️',
                label: 'View',
                title: 'View Pay Slip PDF',
                onClick: (row) => this.viewPayslipPdf(row),
            },
        ],
    };

    tableConfig: VT2_TABLE_CONFIG = {
        tableTitle: '',
        excelFileName: 'user-master',
        columnLabels: {
            firstName: 'First Name',
            lastName: 'Last Name',
            userType: 'Type',
            organisationName: 'Organisation',
            whatsAppNo: 'WhatsApp No',
            empNo: 'Emp. No',
            stopMsg: 'Stop Msg',
            hasLogin: 'Has Login',
        },
        excludeFields: [
            '_id', 'middleName', 'name', 'masterL2Ids',
            'isActive', 'createdAt', 'updatedAt',
            'esiNo', 'dob', 'department', 'role', 'bu',
            'userName',
        ],
        isSortable: true,
        showFormulaButton: false,
        showSummationButton: false,
        showHeaderGroupButton: false,
    };

    constructor(private apiService: ApiRequestsService) {}

    ngOnInit(): void {
        this.tableConfig = {
            ...this.tableConfig,
            actionButtons: [
                {
                    icon: '✏️',
                    label: 'Edit',
                    title: 'Edit user',
                    onClick: (row) => this.openEdit(row),
                },
                {
                    icon: '🔑',
                    label: 'Set Login',
                    title: 'Create/update login credentials (internal users only)',
                    onClick: (row) => {
                        if (row.userType === 'internal') this.openSetLogin(row);
                    },
                },
                {
                    icon: '📄',
                    label: 'Pay Slips',
                    title: 'View pay slips (internal users only)',
                    onClick: (row) => {
                        if (row.userType === 'internal' && row.empNo) this.showPayslips(row);
                    },
                },
                {
                    icon: '🗑️',
                    label: 'Remove',
                    title: 'Remove user',
                    onClick: (row) => this.deactivate(row),
                },
            ],
        };
        this.loadUsers();
        this.loadGenericGroups();
    }

    ngAfterViewInit(): void {
        if (this.addModalRef) {
            document.body.appendChild(this.addModalRef.nativeElement);
            this.addModalInst = new bootstrap.Modal(this.addModalRef.nativeElement);
        }
        if (this.editModalRef) {
            document.body.appendChild(this.editModalRef.nativeElement);
            this.editModalInst = new bootstrap.Modal(this.editModalRef.nativeElement);
        }
        if (this.payslipModalRef) {
            document.body.appendChild(this.payslipModalRef.nativeElement);
            this.payslipModalInst = new bootstrap.Modal(this.payslipModalRef.nativeElement);
        }
        if (this.credentialsModalRef) {
            document.body.appendChild(this.credentialsModalRef.nativeElement);
            this.credModalInst = new bootstrap.Modal(this.credentialsModalRef.nativeElement);
        }
    }

    ngOnDestroy(): void {
        [this.addModalInst, this.editModalInst, this.payslipModalInst, this.credModalInst].forEach(m => m?.hide());
        [this.addModalRef, this.editModalRef, this.payslipModalRef, this.credentialsModalRef].forEach(ref => {
            const el = ref?.nativeElement;
            if (el?.parentNode === document.body) document.body.removeChild(el);
        });
    }

    // ── Data loading ─────────────────────────────────────────────────────────

    loadUsers(): void {
        this.isLoadingUsers = true;
        this.apiService.getApiCall('api/v1/user-master').subscribe({
            next: (res: any) => {
                this.isLoadingUsers = false;
                const raw: any[] = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.users = raw.map(u => ({
                    firstName: '', middleName: '', lastName: '',
                    name: '', whatsAppNo: '', userType: 'external' as const,
                    organisationName: '', stopMsg: false, masterL2Ids: [],
                    userName: '',
                    empNo: '', esiNo: '', dob: '', department: '', role: '', bu: '',
                    ...u,
                    hasLogin: !!u.userName,
                }));
            },
            error: () => { this.isLoadingUsers = false; },
        });
    }

    loadGenericGroups(): void {
        this.isLoadingGroups = true;
        this.apiService.getApiCall('api/v1/generic-master').subscribe({
            next: (res: any) => {
                const masters: any[] = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.genericGroups = masters.map(m => ({ master: m, values: [], selectedId: '' }));
                if (!masters.length) { this.isLoadingGroups = false; return; }
                let pending = masters.length;
                masters.forEach((m, i) => {
                    this.apiService.getApiCall(`api/v1/generic-master/${m._id}/values`).subscribe({
                        next: (r: any) => {
                            this.genericGroups[i].values = r?.errorCode === this.apiService.NO_ERROR ? (r.Data || []) : [];
                            if (--pending === 0) this.isLoadingGroups = false;
                        },
                        error: () => { if (--pending === 0) this.isLoadingGroups = false; },
                    });
                });
            },
            error: () => { this.isLoadingGroups = false; },
        });
    }

    // ── Group filtering ───────────────────────────────────────────────────────

    private filterGroupsForType(groups: GenericGroup[], userType: 'internal' | 'external'): GenericGroup[] {
        return groups.filter(g => {
            const scope = g.master.userTypeScope || 'none';
            return scope !== 'disabled' && (scope === 'none' || scope === userType);
        });
    }

    onAddUserTypeChange(): void {
        this.addGroups = this.filterGroupsForType(
            this.genericGroups.map(g => ({
                ...g,
                selectedId: this.addGroups.find(ag => ag.master._id === g.master._id)?.selectedId || '',
            })),
            this.newUser.userType
        );
    }

    onEditUserTypeChange(): void {
        this.editGroups = this.filterGroupsForType(
            this.genericGroups.map(g => ({
                ...g,
                selectedId: this.editGroups.find(eg => eg.master._id === g.master._id)?.selectedId || '',
            })),
            this.editUser.userType
        );
    }

    // ── Add ──────────────────────────────────────────────────────────────────

    openAdd(): void {
        this.newUser = EMPTY_USER();
        this.addGroups = this.filterGroupsForType(
            this.genericGroups.map(g => ({ ...g, selectedId: '' })),
            this.newUser.userType
        );
        this.addError = '';
        this.addModalInst?.show();
    }

    saveUser(): void {
        if (!this.newUser.firstName.trim()) { this.addError = 'First name is required.'; return; }
        if (!this.newUser.lastName.trim()) { this.addError = 'Last name is required.'; return; }
        if (!this.newUser.whatsAppNo.trim()) { this.addError = 'WhatsApp number is required.'; return; }
        if (this.newUser.userType === 'internal' && !this.newUser.empNo.trim()) { this.addError = 'Employee No. is required for internal users.'; return; }

        this.isSavingAdd = true;
        this.addError = '';
        const body = { ...this.newUser, masterL2Ids: this.addGroups.map(g => g.selectedId).filter(Boolean) };
        this.apiService.postApiCall('api/v1/user-master', body).subscribe({
            next: (res: any) => {
                this.isSavingAdd = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.addModalInst?.hide();
                    this.loadUsers();
                } else {
                    this.addError = res?.errorDescription || 'Failed to add user.';
                }
            },
            error: () => { this.isSavingAdd = false; this.addError = 'Failed to add user.'; },
        });
    }

    // ── Edit ─────────────────────────────────────────────────────────────────

    openEdit(user: User): void {
        this.editUser = {
            _id: user._id,
            firstName: user.firstName || '',
            middleName: user.middleName || '',
            lastName: user.lastName || '',
            whatsAppNo: user.whatsAppNo || '',
            userType: user.userType || 'external',
            organisationName: user.organisationName || '',
            stopMsg: !!user.stopMsg,
            empNo: user.empNo || '',
            esiNo: user.esiNo || '',
            dob: user.dob || '',
            department: user.department || '',
            role: user.role || '',
            bu: user.bu || '',
        };
        this.editGroups = this.filterGroupsForType(
            this.genericGroups.map(g => ({
                ...g,
                selectedId: g.values.find(v => (user.masterL2Ids || []).includes(v._id))?._id ?? '',
            })),
            this.editUser.userType
        );
        this.editError = '';
        this.editModalInst?.show();
    }

    updateUser(): void {
        if (!this.editUser.firstName.trim()) { this.editError = 'First name is required.'; return; }
        if (!this.editUser.lastName.trim()) { this.editError = 'Last name is required.'; return; }
        if (!this.editUser.whatsAppNo.trim()) { this.editError = 'WhatsApp number is required.'; return; }

        this.isSavingEdit = true;
        this.editError = '';
        const body = { ...this.editUser, masterL2Ids: this.editGroups.map(g => g.selectedId).filter(Boolean) };
        this.apiService.putApiCall(`api/v1/user-master/${this.editUser._id}`, body).subscribe({
            next: (res: any) => {
                this.isSavingEdit = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.editModalInst?.hide();
                    this.loadUsers();
                } else {
                    this.editError = res?.errorDescription || 'Failed to update user.';
                }
            },
            error: () => { this.isSavingEdit = false; this.editError = 'Failed to update user.'; },
        });
    }

    // ── Delete ───────────────────────────────────────────────────────────────

    deactivate(user: User): void {
        if (!confirm(`Remove "${user.firstName} ${user.lastName}"?`)) return;
        this.apiService.deleteApiCall(`api/v1/user-master/${user._id}`).subscribe({
            next: (res: any) => {
                if (res?.errorCode === this.apiService.NO_ERROR) this.loadUsers();
            },
            error: () => {},
        });
    }

    // ── Payslips ─────────────────────────────────────────────────────────────

    showPayslips(user: User): void {
        this.payslipTitle = `${user.firstName} ${user.lastName} — Emp. No: ${user.empNo}`;
        this.employeePayslips = [];
        this.payslipError = '';
        this.isLoadingPayslips = true;
        this.payslipTableConfig = { ...this.payslipTableConfig, tableTitle: `Emp. No.: ${user.empNo}   |   ESI No.: ${user.esiNo || '—'}` };
        this.payslipModalInst?.show();
        this.apiService.getApiCall('api/v1/payslip/list', JSON.stringify({ empNo: user.empNo })).subscribe({
            next: (res: any) => {
                this.isLoadingPayslips = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.employeePayslips = res.Data || [];
                } else {
                    this.payslipError = res?.errorDescription || 'Unable to load pay slips.';
                }
            },
            error: () => { this.isLoadingPayslips = false; this.payslipError = 'Unable to load pay slips.'; },
        });
    }

    viewPayslipPdf(payslip: any): void {
        this.apiService.getApiCall('api/v1/payslip/view', JSON.stringify({ id: payslip._id })).subscribe({
            next: (res: any) => {
                if (res?.errorCode === this.apiService.NO_ERROR && res.Data?.url) {
                    window.open(res.Data.url, '_blank');
                } else {
                    this.payslipError = res?.errorDescription || 'Unable to open pay slip PDF.';
                }
            },
            error: () => { this.payslipError = 'Unable to open pay slip PDF.'; },
        });
    }

    // ── Bulk Import ──────────────────────────────────────────────────────────

    async downloadTemplate(): Promise<void> {
        if (this.isLoadingGroups) {
            alert('Master data is still loading. Please wait a moment and try again.');
            return;
        }

        this.isDownloadingTemplate = true;
        const { Workbook } = await import('exceljs');

        const fixedHeaders = [
            'firstName', 'middleName', 'lastName', 'whatsAppNo', 'userType',
            'organisationName', 'stopMsg', 'empNo', 'esiNo', 'dob',
        ];
        const masterHeaders = this.genericGroups.map(g => g.master.masterName);
        const allHeaders = [...fixedHeaders, ...masterHeaders];

        const workbook = new Workbook();
        const ws = workbook.addWorksheet('Users');

        // Header row
        const hRow = ws.addRow(allHeaders);
        hRow.eachCell(cell => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
        });

        // 200 blank data rows
        for (let i = 0; i < 200; i++) ws.addRow([]);

        // Column widths
        allHeaders.forEach((h, i) => {
            ws.getColumn(i + 1).width = Math.max((h as string).length + 4, 18);
        });

        // Column index (0-based) → Excel column letter
        const colLetter = (idx: number): string => {
            let s = '';
            let n = idx + 1;
            while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
            return s;
        };

        // Add dropdown validation to a column range
        // Cast to any: dataValidations exists at runtime but is missing from ExcelJS type defs in this version
        const wsDV = ws as any;
        const addDropdown = (colIdx: number, options: string[]): void => {
            if (!options.length) return;
            const col = colLetter(colIdx);
            wsDV.dataValidations.add(`${col}2:${col}201`, {
                type: 'list',
                allowBlank: true,
                formulae: [`"${options.join(',')}"`],
                showErrorMessage: true,
                errorStyle: 'error',
                errorTitle: 'Invalid value',
                error: 'Please select a value from the dropdown list.',
            });
        };

        addDropdown(fixedHeaders.indexOf('userType'), ['internal', 'external']);
        addDropdown(fixedHeaders.indexOf('stopMsg'), ['Yes', 'No']);
        this.genericGroups.forEach((g, gi) => {
            addDropdown(fixedHeaders.length + gi, g.values.map(v => v.value));
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer as ArrayBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'user-master-template.xlsx';
        a.click();
        URL.revokeObjectURL(url);
        this.isDownloadingTemplate = false;
    }

    uploadExcel(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!input) return;
        input.value = ''; // reset so same file can be re-uploaded
        if (!file) return;

        this.isUploading = true;
        this.uploadSummary = null;

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target!.result as ArrayBuffer);
            const wb = XLSX.read(data, { type: 'array', cellDates: true });
            const ws = wb.Sheets['Users'];
            if (!ws) {
                this.isUploading = false;
                this.uploadSummary = { inserted: 0, errors: [{ row: 0, message: 'Sheet "Users" not found. Please use the downloaded template.' }] };
                return;
            }

            const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
            const headers: string[] = rows[0] || [];

            const fixedHeaders = [
                'firstName', 'middleName', 'lastName', 'whatsAppNo', 'userType',
                'organisationName', 'stopMsg', 'empNo', 'esiNo', 'dob',
            ];

            const users: any[] = [];
            for (let i = 1; i < rows.length; i++) {
                const row = rows[i];
                if (!row || row.every((c: any) => c === '' || c === undefined || c === null)) continue;

                const getRaw = (col: string): any => {
                    const idx = headers.indexOf(col);
                    return idx >= 0 ? row[idx] : undefined;
                };
                const getStr = (col: string): string => {
                    const v = getRaw(col);
                    if (v === undefined || v === null) return '';
                    return String(v).trim();
                };
                const getDate = (col: string): string => {
                    const v = getRaw(col);
                    if (!v) return '';
                    if (v instanceof Date) {
                        const y = v.getFullYear();
                        const m = String(v.getMonth() + 1).padStart(2, '0');
                        const d = String(v.getDate()).padStart(2, '0');
                        return `${y}-${m}-${d}`;
                    }
                    return String(v).trim();
                };

                const masterL2Ids: string[] = [];
                this.genericGroups.forEach(g => {
                    const colIdx = headers.indexOf(g.master.masterName);
                    if (colIdx < 0) return;
                    const cellVal = String(row[colIdx] ?? '').trim();
                    if (!cellVal) return;
                    const match = g.values.find(v => v.value === cellVal);
                    if (match) masterL2Ids.push(match._id);
                });

                users.push({
                    firstName: getStr('firstName'),
                    middleName: getStr('middleName'),
                    lastName: getStr('lastName'),
                    whatsAppNo: getStr('whatsAppNo'),
                    userType: getStr('userType') || 'external',
                    organisationName: getStr('organisationName'),
                    stopMsg: getStr('stopMsg').toLowerCase() === 'yes',
                    empNo: getStr('empNo'),
                    esiNo: getStr('esiNo'),
                    dob: getDate('dob'),
                    masterL2Ids,
                });
            }

            if (!users.length) {
                this.isUploading = false;
                this.uploadSummary = { inserted: 0, errors: [{ row: 0, message: 'No data rows found in the file.' }] };
                return;
            }

            this.apiService.postApiCall('api/v1/user-master/bulk', { users }).subscribe({
                next: (res: any) => {
                    this.isUploading = false;
                    if (res?.errorCode === this.apiService.NO_ERROR) {
                        this.uploadSummary = { inserted: res.Data?.inserted ?? 0, errors: res.Data?.errors ?? [] };
                        if (res.Data?.inserted > 0) this.loadUsers();
                    } else {
                        this.uploadSummary = { inserted: 0, errors: [{ row: 0, message: res?.errorDescription || 'Upload failed.' }] };
                    }
                },
                error: () => {
                    this.isUploading = false;
                    this.uploadSummary = { inserted: 0, errors: [{ row: 0, message: 'Upload failed. Please try again.' }] };
                },
            });
        };
        reader.readAsArrayBuffer(file);
    }

    // ── Credentials ──────────────────────────────────────────────────────────

    openSetLogin(user: User): void {
        this.credUser = { _id: user._id, name: `${user.firstName} ${user.lastName}`.trim() };
        this.credForm = { userName: user.userName || '', pwd: '', showPwd: false };
        this.credError = '';
        this.credModalInst?.show();
    }

    saveCredentials(): void {
        if (!this.credForm.userName.trim()) { this.credError = 'Username is required.'; return; }
        if (!this.credForm.pwd.trim()) { this.credError = 'Password is required.'; return; }
        this.isSavingCred = true;
        this.credError = '';
        this.apiService.putApiCall(`api/v1/user-master/${this.credUser._id}/credentials`, {
            userName: this.credForm.userName.trim(),
            pwd: this.credForm.pwd.trim(),
        }).subscribe({
            next: (res: any) => {
                this.isSavingCred = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.credModalInst?.hide();
                    this.loadUsers();
                } else {
                    this.credError = res?.errorDescription || 'Failed to save credentials.';
                }
            },
            error: () => { this.isSavingCred = false; this.credError = 'Failed to save credentials.'; },
        });
    }
}
